<script setup lang="ts">
import {
  AuthorizationError,
  createSelectOptions,
  FRONTEND_CONTENT_TYPES,
  NEWS_TYPE_ENUM,
  POSTIT_CATEGORY_ENUM,
  type FrontContent,
  type FrontContentType,
  type OverviewContentType,
  type Pagination,
  type V1GetOptions,
} from "@gasparigit/omnia-sdk";
import { FRONT_TYPES_MAP } from "~/lib/const";
import { isNotAllowed, link } from "~/lib/utils/contents";
import { processError } from "~/lib/utils/errors";
import { defineSeo } from "~/lib/utils/seo";

const omniaSdk = useOmniaSdk();
const route = useRoute();
const hubId = useParamId();
const refMenus = useRefMenus();

const selectedTab = ref<FrontContentType | "all">(
  (route.query.section as FrontContentType) || "all"
);
const page = usePage();

const defaultOptions: V1GetOptions = {
  future: true,
  past: true,
  order_mode:
    (route.query["order"] as "asc" | "desc") ||
    (selectedTab.value === "scadenzario" ? "asc" : "desc"),
  news_types: null,
  category_types: null,
  publishers: null,
  from_date: route.query["from"]
    ? new Date(route.query["from"] as string)
    : selectedTab.value === "scadenzario"
    ? new Date()
    : null,
  to_date: route.query["to"] ? new Date(route.query["to"] as string) : null,
};

const options = ref<V1GetOptions>(defaultOptions);

const { data: area, error: areaError } = await useAsyncData(
  `area-${route.params.area_slug}`,
  async () => {
    if (!route.params.area_slug) return;

    try {
      return await omniaSdk.v1.areas.get(route.params.area_slug as string);
    } catch (error) {
      processError(error);
    }
  }
);

if (areaError.value) processError(areaError.value);

const {
  data: hub,
  error: hubError,
  status: hubStatus,
} = await useAsyncData(`hub-${hubId}`, async () => {
  try {
    return await omniaSdk.v1.hubs.get(hubId, { full: true });
  } catch (error) {
    processError(error);
  }
});

if (hubError.value) processError(hubError.value);

defineSeo(hub.value);

const { data: menus } = await useAsyncData(
  `ref-menus-${refMenus?.join(".")}`,
  async () => {
    if (refMenus.length === 0) return false;

    try {
      return await Promise.all(refMenus.map((id) => omniaSdk.v1.menus.get(id)));
    } catch (error) {
      console.log(error);
    }

    return false;
  }
);

const { data: publishers } = useAsyncData(
  "all-publishers",
  async () => {
    try {
      if (
        !(selectedTab.value === "normativa" || selectedTab.value === "portali")
      )
        return null;

      return await omniaSdk.v1.publishers.list({
        hub: hubId,
        type: selectedTab.value === "normativa" ? "norme" : "links",
      });
    } catch (error) {
      processError(error);
    }
  },
  {
    watch: [selectedTab],
  }
);

const { data, status, refresh } = useAsyncData(
  () => `hub-${hubId}-data`,
  async () => {
    try {
      if (selectedTab.value === "all") {
        return await omniaSdk.v1.contents.all(
          { hub: hubId },
          { pagination: "cursor" }
        );
      } else {
        return await omniaSdk.v1.contents.list(
          { hub: hubId },
          selectedTab.value,
          {
            pagination: selectedTab.value === "pratiche" ? "none" : "classic",
            page: page.value,
          },
          options.value
        );
      }
    } catch (error) {
      if (error instanceof AuthorizationError) return { allowed: false };
      processError(error);
    }
  }
);

const { data: highlighted } = useAsyncData(
  `hub-${hubId}-highlighted`,
  async () => {
    try {
      if (selectedTab.value === "all")
        return await omniaSdk.v1.contents.highlighted(
          { hub: hubId },
          { pagination: "none" }
        );
      return null;
    } catch (error) {
      if (!(error instanceof AuthorizationError)) {
        processError(error);
      }
    }
  }
);

watch(
  () => route.query,
  (val) => {
    if (selectedTab.value !== val.section) {
      selectedTab.value = (route.query.section as FrontContentType) || "all";
      refresh();
    }

    if (val.page && page.value !== parseInt(val.page as string)) {
      page.value = parseInt(val.page as string);
      refresh();
    }
  }
);

watch(page, (val) => {
  useRouter().push({
    query: {
      ...route.query,
      section: selectedTab.value,
      page: val,
    },
  });
});

watch(
  options,
  (val) => {
    useRouter().push({
      query: {
        ...route.query,
        section: selectedTab.value,
        page: page.value,
        order: val.order_mode,
        from: val.from_date?.toISOString(),
        to: val.to_date?.toISOString(),
      },
    });

    refresh();
  },
  { deep: true }
);

const onChangeTab = () => {
  page.value = 1;

  const now = new Date();

  options.value = {
    ...defaultOptions,
    order_mode: selectedTab.value === "scadenzario" ? "asc" : "desc",
    from_date: selectedTab.value === "scadenzario" ? now : null,
  };
};

function isAll(data: unknown): data is OverviewContentType {
  return data && selectedTab.value === "all";
}

function isFrontContent(data: unknown): data is Pagination<FrontContent> {
  return data && selectedTab.value !== "all";
}

function openReadAll(frontType: string) {
  selectedTab.value = frontType as FrontContentType;
  onChangeTab();

  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "smooth",
  });
}
</script>

<template>
  <FrontBreadcrumHero
    :breadcrumbs="
      [
        area && { href: link({ area }), title: area.title },
        ...(menus
          ? menus.map((m, i) => ({
              title: m.title,
              href: link({
                area,
                chain: menus ? menus.slice(0, i + 1) : undefined,
              }),
            }))
          : []),
        { title: hub?.title },
      ].filter((f) => !!f)
    "
    :title="hub?.title"
    :abstract="hub?.abstract"
    :search="false"
    :back-url="
      area ? link({ area, chain: menus ? menus : undefined }) : undefined
    "
    :skeleton="!hub && hubStatus === 'pending'"
  />

  <div class="main-content mb-10">
    <FrontUnauthorized
      v-if="isNotAllowed(data)"
      :link-config="{ area, chain: menus ? menus : undefined, hub }"
    />
    <template v-else>
      <FrontContentBodyWithFiles v-if="hub.body?.length" :text="hub.body" />
      <Tabs
        v-model="selectedTab"
        default-value="all"
        @update:model-value="onChangeTab"
      >
        <TabsList class="tab-list">
          <TabsTrigger value="all" class="tab-nav">Tutto</TabsTrigger>
          <template v-for="tab in Object.values(FRONT_TYPES_MAP)">
            <TabsTrigger
              v-if="
                FRONT_TYPES_MAP[tab.type].show_in_tabs &&
                hub?.contents_count &&
                hub.contents_count[tab.type] > 0
              "
              :key="tab.type"
              :value="tab.type"
              class="tab-nav"
              >{{ FRONTEND_CONTENT_TYPES[tab.type] }}</TabsTrigger
            >
          </template>
        </TabsList>
        <TabsContent value="all">
          <FrontContentsOverview
            v-if="status !== 'pending' && isAll(data)"
            v-model="data"
            v-model:highlighted="highlighted"
            :settings="hub?.settings"
            :link-config="{ area, hub, chain: menus ? menus : undefined }"
            :open-read-all="openReadAll"
            :read-all-to="(frontType) => link({ area, hub, frontType })"
          />
          <Skeleton v-else class="w-full h-52 rounded-lg" />
        </TabsContent>
        <template v-for="tab in hub?.settings" :key="`content-${tab.type}`">
          <TabsContent :value="tab.type">
            <FrontContentsList
              v-if="status !== 'pending' && isFrontContent(data)"
              :key="tab.type"
              v-model:data="data"
              v-model:page="page"
              :title="FRONTEND_CONTENT_TYPES[tab.type]"
              :icon="FRONT_TYPES_MAP[tab.type].icon"
              :link-config="{ area, hub, chain: menus ? menus : undefined }"
              :show-children="
                tab.type !== 'pratiche' || data?.data?.length == 1
              "
              :has-side="selectedTab !== 'pratiche'"
              :no-contents-text="
                tab.type === 'scadenzario'
                  ? 'Per questo argomento non sono previste scadenze successive alla data di oggi. Modifica i filtri per visualizzare le scadenze con data antecedente'
                  : null
              "
              @update:page="refresh()"
            >
              <template #side>
                <FrontCheckboxCard
                  v-if="selectedTab === 'postit'"
                  v-model="options.category_types"
                  base-key="category_types"
                  :options="createSelectOptions(POSTIT_CATEGORY_ENUM)"
                />
                <FrontCheckboxCard
                  v-if="selectedTab === 'notizie'"
                  v-model="options.news_types"
                  base-key="news_type"
                  :options="createSelectOptions(NEWS_TYPE_ENUM)"
                />

                <FrontDateFilter
                  v-if="
                    ['scadenzario', 'notizie', 'quesiti'].includes(selectedTab)
                  "
                  v-model="options"
                />

                <FrontCheckboxCard
                  v-if="
                    (selectedTab === 'portali' ||
                      selectedTab === 'normativa') &&
                    publishers?.data.length
                  "
                  v-model="options.publishers"
                  base-key="publisher"
                  name="publisher"
                  :options="
                    publishers.data.map((p) => ({
                      label: p.title,
                      value: p.id,
                    }))
                  "
                />

                <FrontRadioGroupCard
                  v-if="selectedTab !== 'pratiche'"
                  v-model="options.order_mode"
                  base-key="order"
                />
              </template>
            </FrontContentsList>
            <Skeleton v-else class="w-full h-52 rounded-lg" />
          </TabsContent>
        </template>
      </Tabs>
    </template>
  </div>

  <AdminLinkToAdmin :config="{ hub }" />
</template>
